// Fallback initialization for Keycloak with better error handling
import { keycloak } from './keycloak';
import { KEYCLOAK_CONFIG } from '../config/env';
import { logger } from '../utils/logger';

export async function initKeycloakWithFallback(): Promise<boolean> {
  const configuredRole = import.meta.env.VITE_E2E_MOCK_AUTH as string | undefined;
  if (configuredRole) {
    // Allow e2e specs to switch role via ?e2eRole=STUDENT|ADMIN (persisted so
    // it survives client-side navigation and reloads within the test).
    let role = configuredRole;
    try {
      const urlRole = new URLSearchParams(window.location.search).get('e2eRole');
      if (urlRole) {
        localStorage.setItem('e2eRole', urlRole);
        role = urlRole;
      } else {
        const stored = localStorage.getItem('e2eRole');
        if (stored) role = stored;
      }
    } catch {
      // Storage/URL unavailable: fall back to the configured role.
    }
    applyE2eKeycloakMock(role);
    logger.log('🧪 E2E mock auth enabled for role:', role);
    return true;
  }

  try {
    logger.log('🔐 Initializing Keycloak...');
    
    // Proper SSO check to maintain session across page reloads
    const authenticated = await keycloak.init({
      onLoad: 'check-sso',             // Check if user is already logged in
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,         // Disable iframe to avoid CSP issues
      enableLogging: KEYCLOAK_CONFIG.debug,
      pkceMethod: 'S256',
      responseMode: 'query'
    });
    
    logger.log('✅ Keycloak init successful. Authenticated:', authenticated);
    
    if (authenticated) {
      logger.log('🎉 User is authenticated, token expires in:', keycloak.tokenParsed?.exp);
      logger.log('👤 User info:', {
        username: keycloak.tokenParsed?.preferred_username,
        email: keycloak.tokenParsed?.email,
        roles: keycloak.tokenParsed?.realm_access?.roles
      });
    } else {
      logger.log('🔑 User not authenticated, will need to login manually');
    }
    
    return authenticated;
    
  } catch (error: any) {
    logger.error('❌ Keycloak init failed:', error);
    
    // Debug: Check if the endpoints are accessible
    logger.log('🔍 Testing Keycloak endpoints...');
    const wellKnownUrl = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/.well-known/openid_configuration`;
    
    try {
      const response = await fetch(wellKnownUrl);
      if (response.ok) {
        logger.log('✅ Keycloak well-known endpoint is accessible');
        const config = await response.json();
        logger.log('🔧 Keycloak config:', config);
      } else {
        logger.error('❌ Keycloak well-known endpoint returned:', response.status);
      }
    } catch (fetchError) {
      logger.error('❌ Cannot access Keycloak well-known endpoint:', fetchError);
    }
    
    // Allow app to continue without Keycloak
    logger.warn('⚠️ Running app in fallback mode without Keycloak');
    return false;
  }
}

function applyE2eKeycloakMock(role: string): void {
  const normalized = role.toUpperCase();
  const roles =
    normalized === 'STUDENT'
      ? ['STUDENT']
      : normalized === 'ADMIN'
        ? ['ADMIN', 'TEACHER']
        : ['TEACHER'];
  const now = Math.floor(Date.now() / 1000);
  Object.assign(keycloak, {
    authenticated: true,
    token: 'e2e-mock-token',
    tokenParsed: {
      sub: 'e2e-user-id',
      preferred_username: 'e2e-user',
      email: 'e2e@test.local',
      name: 'E2E User',
      exp: now + 3600,
      realm_access: { roles },
    },
    updateToken: async () => false,
    login: async () => undefined,
    logout: async () => undefined,
  });
}
